import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { isEmptyString } from '@/utils/string';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { getGoalsQueryOptions } from '@/services/db/goal/queries';
import { toConversions } from './adapter';
import { conversionsView } from './rows';
import { ConversionsLoadError } from './cells';
import { ConversionsTable } from './conversions-table';

/**
 * The Conversions tab of the data index: every goal the workspace tracks,
 * read as the conversion it counts.
 *
 * The goals list is not paginated, so the whole set arrives in one read and
 * the narrowing happens here — searched first, then cut into pages, so a
 * search narrows the whole list rather than the page someone happens to be
 * on.
 */

const CONVERSIONS_PARSERS = {
  page: parseAsInteger.withDefault(1),
  search: parseAsString.withDefault(''),
};

export function ConversionsBody() {
  const workspaceId = requireWorkspaceId();
  const queryClient = useQueryClient();
  // Searching replaces, so it does not fill the history; turning a page
  // pushes, so Back returns to the page it came from. The same two histories
  // the tabs beside this one keep.
  const [params, setParams] = useQueryStates(CONVERSIONS_PARSERS, { history: 'replace' });

  const goals = useQuery(getGoalsQueryOptions(workspaceId, queryClient));
  const conversions = useMemo(() => toConversions(goals.data ?? []), [goals.data]);
  const view = useMemo(
    () => conversionsView(conversions, params.search, params.page),
    [conversions, params.page, params.search],
  );

  // A failed read is not an empty workspace, and the table's empty state
  // would say it was.
  if (goals.isError) {
    return <ConversionsLoadError />;
  }

  return (
    <ConversionsTable
      workspaceId={workspaceId}
      rows={view.rows}
      total={view.total}
      isLoading={goals.isPending}
      search={params.search}
      // A new search is a new set of rows, so the page it is read from is the
      // first one: the page someone was on belongs to a list that no longer
      // exists.
      onSearchChange={(search) => void setParams({ page: null, search: isEmptyString(search) ? null : search })}
      page={params.page}
      onPageChange={(page) => void setParams({ page }, { history: 'push' })}
    />
  );
}
